import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Mic, Brain, Sparkles, ChevronRight } from 'lucide-react';

const ONIRVA_ICON = "https://customer-assets.emergentagent.com/job_200ec0e0-0820-4417-b1b5-b6286eb18bc4/artifacts/c3nyk9fu_80AC8250-CA42-4BF9-BA0D-F1211AA6A764.png";

export default function Landing() {
  const navigate = useNavigate();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Hero gradient background */}
      <div className="absolute inset-0 hero-gradient" />
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1612431318619-9abdb218253b?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.3)'
        }}
      />
      
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-6 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src={ONIRVA_ICON} alt="ONIRVA" className="w-10 h-10 rounded-xl" />
            <span className="text-2xl font-serif font-bold tracking-tight">ONIRVA</span>
          </div>
          <Button 
            variant="ghost" 
            className="text-muted-foreground hover:text-foreground"
            onClick={() => navigate('/login')}
            data-testid="header-login-btn"
          >
            Sign In
          </Button>
        </header>
        
        {/* Hero Section */}
        <main className="flex-1 flex flex-col items-center justify-center px-6 text-center pb-20">
          <div className="max-w-xl mx-auto stagger-fade-in">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm text-muted-foreground mb-6">
                <Sparkles className="w-4 h-4 text-primary" />
                <span>Personal Dream Reflection</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight mb-6 leading-tight">
                Explore the Patterns<br />
                <span className="text-gradient">Within Your Dreams</span>
              </h1>
              
              <p className="text-base sm:text-lg text-muted-foreground max-w-md mx-auto leading-relaxed">
                A quiet space for dream reflection that learns your inner landscape, 
                remembers your journey, and gently surfaces patterns over time.
              </p>
            </div>
            
            {/* Feature pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full glass text-sm">
                <Mic className="w-4 h-4 text-primary" />
                <span>Voice Capture</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full glass text-sm">
                <Brain className="w-4 h-4 text-primary" />
                <span>Personal Context</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full glass text-sm">
                <Sparkles className="w-4 h-4 text-primary" />
                <span>Gentle Insights</span>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="rounded-full px-8 py-6 text-lg glow-primary hover:scale-105 transition-transform"
                onClick={() => navigate('/register')}
                data-testid="get-started-btn"
              >
                Get Started Free
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              
              <Button 
                size="lg"
                variant="outline"
                className="rounded-full px-8 py-6 text-lg border-white/20 hover:bg-white/5"
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
            </div>
          </div>
        </main>
        
        {/* Footer */}
        <footer className="p-6 text-center text-sm text-muted-foreground">
          <p>Your dreams are private and encrypted. We never share your data.</p>
        </footer>
      </div>
    </div>
  );
}
